/**
 * Operations Dashboard - Phases 4-6
 * - SOP & Training System
 * - Unit Economics & Breakeven
 * - Sales Velocity & Hourly Analysis
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen, Users, GraduationCap, Calculator, TrendingUp, Clock,
  Plus, Edit2, Trash2, Save, X, RefreshCw, CheckCircle2, AlertTriangle,
  FileText, ChefHat, Sunrise, Sunset, AlertCircle, Settings, Sparkles,
  Package, DollarSign, Target, BarChart3, Activity, Zap, Calendar,
  ChevronRight, Award, Percent, PieChart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatCurrencyFull = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

// Category icons
const categoryIcons = {
  opening: Sunrise,
  closing: Sunset,
  food_prep: ChefHat,
  customer_service: Users,
  emergency: AlertCircle,
  equipment: Settings,
  cleaning: Sparkles,
  inventory: Package
};

// Heatmap intensity colors
const intensityColors = {
  very_high: 'bg-green-600 dark:bg-green-500',
  high: 'bg-green-400 dark:bg-green-600',
  medium: 'bg-yellow-400 dark:bg-yellow-600',
  low: 'bg-orange-300 dark:bg-orange-700',
  very_low: 'bg-gray-200 dark:bg-gray-700'
};

// SOP Form Modal
function SOPFormModal({ isOpen, onClose, onSave, editingSOP, categories }) {
  const [formData, setFormData] = useState({
    title: '',
    category: 'opening',
    description: '',
    steps: [''],
    time_estimate_minutes: '',
    frequency: 'daily',
    safety_notes: ''
  });

  useEffect(() => {
    if (editingSOP) {
      setFormData({
        title: editingSOP.title || '',
        category: editingSOP.category || 'opening',
        description: editingSOP.description || '',
        steps: editingSOP.steps || [''],
        time_estimate_minutes: editingSOP.time_estimate_minutes || '',
        frequency: editingSOP.frequency || 'daily',
        safety_notes: editingSOP.safety_notes || ''
      });
    } else {
      setFormData({
        title: '',
        category: 'opening',
        description: '',
        steps: [''],
        time_estimate_minutes: '',
        frequency: 'daily',
        safety_notes: ''
      });
    }
  }, [editingSOP, isOpen]);

  const addStep = () => setFormData({ ...formData, steps: [...formData.steps, ''] });
  const removeStep = (idx) => setFormData({ ...formData, steps: formData.steps.filter((_, i) => i !== idx) });
  const updateStep = (idx, value) => {
    const newSteps = [...formData.steps];
    newSteps[idx] = value;
    setFormData({ ...formData, steps: newSteps });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanSteps = formData.steps.filter(s => s.trim());
    if (!formData.title || cleanSteps.length === 0) {
      toast.error('Please add a title and at least one step');
      return;
    }
    onSave({
      ...formData,
      steps: cleanSteps,
      time_estimate_minutes: formData.time_estimate_minutes ? parseInt(formData.time_estimate_minutes) : null
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingSOP ? 'Edit SOP' : 'Create New SOP'}</DialogTitle>
          <DialogDescription>Standard Operating Procedure</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Morning Opening Checklist"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categories || {}).map(([key, info]) => (
                    <SelectItem key={key} value={key}>{info.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this procedure..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Steps *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addStep}>
                <Plus className="w-4 h-4 mr-1" /> Add Step
              </Button>
            </div>
            <div className="space-y-2">
              {formData.steps.map((step, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground w-6">{idx + 1}.</span>
                  <Input
                    value={step}
                    onChange={(e) => updateStep(idx, e.target.value)}
                    placeholder={`Step ${idx + 1}...`}
                  />
                  {formData.steps.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeStep(idx)}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Time Estimate (minutes)</Label>
              <Input
                type="number"
                min="1"
                value={formData.time_estimate_minutes}
                onChange={(e) => setFormData({ ...formData, time_estimate_minutes: e.target.value })}
                placeholder="e.g., 15"
              />
            </div>
            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="as-needed">As Needed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Safety Notes</Label>
            <Textarea
              value={formData.safety_notes}
              onChange={(e) => setFormData({ ...formData, safety_notes: e.target.value })}
              placeholder="Any safety considerations..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">
              <Save className="w-4 h-4 mr-2" />
              {editingSOP ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export default function OperationsDashboardTab() {
  const [activeTab, setActiveTab] = useState('sop');
  const [loading, setLoading] = useState(true);
  
  // SOP & Training state
  const [sops, setSops] = useState([]);
  const [trainingData, setTrainingData] = useState(null);
  const [sopCategories, setSopCategories] = useState({});
  const [showSOPModal, setShowSOPModal] = useState(false);
  const [editingSOP, setEditingSOP] = useState(null);
  
  // Unit Economics state
  const [unitEconomics, setUnitEconomics] = useState(null);
  const [breakevenDefaults, setBreakevenDefaults] = useState(null);
  const [breakevenResult, setBreakevenResult] = useState(null);
  const [breakevenInputs, setBreakevenInputs] = useState({
    fixed_costs_monthly: '',
    variable_cost_percent: '',
    avg_ticket: ''
  });
  
  // Sales Velocity state
  const [hourlyData, setHourlyData] = useState(null);
  const [productVelocity, setProductVelocity] = useState(null);
  const [dailyPattern, setDailyPattern] = useState(null);

  const fetchSOPs = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/operations/sop');
      setSops(res.data.sops || []);
      setSopCategories(res.data.categories || {});
    } catch (err) {
      console.error('Failed to fetch SOPs:', err);
    }
  }, []);

  const fetchTrainingData = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/operations/training/dashboard');
      setTrainingData(res.data);
    } catch (err) {
      console.error('Failed to fetch training data:', err);
    }
  }, []);

  const fetchUnitEconomics = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/operations/unit-economics');
      setUnitEconomics(res.data);
    } catch (err) {
      console.error('Failed to fetch unit economics:', err);
    }
  }, []);

  const fetchBreakevenDefaults = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/operations/breakeven/defaults');
      setBreakevenDefaults(res.data);
      setBreakevenInputs({
        fixed_costs_monthly: res.data.suggested?.fixed_costs_monthly?.toString() || '',
        variable_cost_percent: res.data.suggested?.variable_cost_percent?.toString() || '',
        avg_ticket: res.data.suggested?.avg_ticket?.toString() || ''
      });
    } catch (err) {
      console.error('Failed to fetch breakeven defaults:', err);
    }
  }, []);

  const fetchSalesVelocity = useCallback(async () => {
    try {
      const [hourly, products, daily] = await Promise.all([
        api.get('/admin/finance/operations/sales-velocity/hourly').then(r => r.data).catch(() => null),
        api.get('/admin/finance/operations/sales-velocity/products').then(r => r.data).catch(() => null),
        api.get('/admin/finance/operations/sales-velocity/daily').then(r => r.data).catch(() => null)
      ]);
      setHourlyData(hourly);
      setProductVelocity(products);
      setDailyPattern(daily);
    } catch (err) {
      console.error('Failed to fetch sales velocity:', err);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchSOPs(),
      fetchTrainingData(),
      fetchUnitEconomics(),
      fetchBreakevenDefaults(),
      fetchSalesVelocity()
    ]);
    setLoading(false);
  }, [fetchSOPs, fetchTrainingData, fetchUnitEconomics, fetchBreakevenDefaults, fetchSalesVelocity]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSaveSOP = async (sopData) => {
    try {
      if (editingSOP) {
        await api.put(`/admin/finance/operations/sop/${editingSOP.sop_id}`, sopData);
      } else {
        await api.post('/admin/finance/operations/sop', sopData);
      }
      
      toast.success(editingSOP ? 'SOP updated' : 'SOP created');
      setShowSOPModal(false);
      setEditingSOP(null);
      fetchSOPs();
      fetchTrainingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save SOP');
    }
  };

  const handleDeleteSOP = async (sopId) => {
    if (!confirm('Deactivate this SOP?')) return;
    try {
      await api.delete(`/admin/finance/operations/sop/${sopId}`);
      toast.success('SOP deactivated');
      fetchSOPs();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const calculateBreakeven = async () => {
    try {
      const res = await api.post('/admin/finance/operations/breakeven', {
        fixed_costs_monthly: parseFloat(breakevenInputs.fixed_costs_monthly) || 0,
        variable_cost_percent: parseFloat(breakevenInputs.variable_cost_percent) || 0,
        avg_ticket: breakevenInputs.avg_ticket ? parseFloat(breakevenInputs.avg_ticket) : null
      });
      setBreakevenResult(res.data);
    } catch (err) {
      toast.error('Failed to calculate breakeven');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="operations-dashboard-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Operations Dashboard</h2>
          <p className="text-muted-foreground">SOPs, Unit Economics & Sales Velocity</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-lg">
          <TabsTrigger value="sop">SOPs & Training</TabsTrigger>
          <TabsTrigger value="economics">Unit Economics</TabsTrigger>
          <TabsTrigger value="velocity">Sales Velocity</TabsTrigger>
        </TabsList>

        {/* SOP & Training Tab */}
        <TabsContent value="sop" className="space-y-4">
          {/* Training Summary */}
          {trainingData && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-500/20 rounded-xl">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total SOPs</p>
                      <p className="text-2xl font-bold">{trainingData.summary?.total_sops || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-500/20 rounded-xl">
                      <GraduationCap className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Completion</p>
                      <p className="text-2xl font-bold">{trainingData.summary?.overall_completion_percent || 0}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-500/20 rounded-xl">
                      <Award className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Fully Trained</p>
                      <p className="text-2xl font-bold">{trainingData.summary?.fully_trained_staff || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-amber-500/20 rounded-xl">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Needs Training</p>
                      <p className="text-2xl font-bold">{trainingData.summary?.needs_training_count || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* SOPs List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Standard Operating Procedures</CardTitle>
                  <CardDescription>{sops.length} procedures documented</CardDescription>
                </div>
                <Button onClick={() => { setEditingSOP(null); setShowSOPModal(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  New SOP
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {sops.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No SOPs created yet</p>
                  <Button className="mt-4" onClick={() => setShowSOPModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First SOP
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(sopCategories).map(([cat, info]) => {
                    const catSOPs = sops.filter(s => s.category === cat);
                    if (catSOPs.length === 0) return null;
                    const IconComponent = categoryIcons[cat] || FileText;
                    return (
                      <div key={cat} className="border rounded-lg overflow-hidden">
                        <div className="bg-muted/50 px-4 py-2 flex items-center gap-2">
                          <IconComponent className="w-4 h-4" />
                          <span className="font-medium">{info.label}</span>
                          <Badge variant="outline" className="ml-auto">{catSOPs.length}</Badge>
                        </div>
                        <div className="divide-y">
                          {catSOPs.map((sop) => (
                            <div key={sop.sop_id} className="px-4 py-3 flex items-center justify-between hover:bg-muted/30">
                              <div>
                                <p className="font-medium">{sop.title}</p>
                                <p className="text-sm text-muted-foreground">
                                  {sop.step_count} steps
                                  {sop.time_estimate_minutes && ` • ${sop.time_estimate_minutes} min`}
                                  {sop.frequency && ` • ${sop.frequency}`}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => { setEditingSOP(sop); setShowSOPModal(true); }}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive"
                                  onClick={() => handleDeleteSOP(sop.sop_id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Staff Training Progress */}
          {trainingData?.staff_completion?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Staff Training Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {trainingData.staff_completion.map((staff) => (
                    <div key={staff.staff_id} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{staff.staff_name}</span>
                        <span className="text-muted-foreground">{staff.completed}/{staff.total_sops} SOPs</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Progress value={staff.completion_percent} className="flex-1 h-2" />
                        <span className={`text-sm font-medium ${
                          staff.completion_percent === 100 ? 'text-green-600' :
                          staff.completion_percent >= 50 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {staff.completion_percent}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Unit Economics Tab */}
        <TabsContent value="economics" className="space-y-4">
          {/* Unit Economics Summary */}
          {unitEconomics && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Customer Acquisition
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Marketing Spend</p>
                      <p className="text-2xl font-bold">{formatCurrency(unitEconomics.acquisition?.marketing_spend)}</p>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">New Customers</p>
                      <p className="text-2xl font-bold">{unitEconomics.acquisition?.new_customers}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-primary/10 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Customer Acquisition Cost (CAC)</p>
                    <p className="text-3xl font-bold text-primary">{formatCurrencyFull(unitEconomics.acquisition?.cac)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{unitEconomics.acquisition?.cac_note}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Customer Lifetime Value
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Avg Order</p>
                      <p className="text-lg font-bold">{formatCurrencyFull(unitEconomics.lifetime_value?.avg_order_value)}</p>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Orders/Customer</p>
                      <p className="text-lg font-bold">{unitEconomics.lifetime_value?.avg_orders_per_customer}</p>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Lifespan</p>
                      <p className="text-lg font-bold">{unitEconomics.lifetime_value?.avg_lifespan_months}mo</p>
                    </div>
                  </div>
                  <div className="p-4 bg-green-500/10 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Customer Lifetime Value (CLV)</p>
                    <p className="text-3xl font-bold text-green-600">{formatCurrencyFull(unitEconomics.lifetime_value?.clv)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{unitEconomics.lifetime_value?.clv_note}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* CLV:CAC Ratio */}
          {unitEconomics && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Unit Economics Health</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className={`p-4 rounded-lg text-center ${
                    unitEconomics.health?.status === 'excellent' ? 'bg-green-100 dark:bg-green-900/30' :
                    unitEconomics.health?.status === 'good' ? 'bg-blue-100 dark:bg-blue-900/30' :
                    unitEconomics.health?.status === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30' :
                    'bg-red-100 dark:bg-red-900/30'
                  }`}>
                    <p className="text-sm text-muted-foreground">CLV:CAC Ratio</p>
                    <p className="text-4xl font-bold">{unitEconomics.ratios?.clv_cac_ratio}:1</p>
                    <p className="text-xs mt-1">Ideal: {unitEconomics.ratios?.ideal_ratio}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Payback Period</p>
                    <p className="text-4xl font-bold">{unitEconomics.ratios?.payback_months}</p>
                    <p className="text-xs mt-1">months to recover CAC</p>
                  </div>
                  <div className={`p-4 rounded-lg ${
                    unitEconomics.health?.status === 'excellent' ? 'bg-green-100 dark:bg-green-900/30' :
                    unitEconomics.health?.status === 'good' ? 'bg-blue-100 dark:bg-blue-900/30' :
                    unitEconomics.health?.status === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30' :
                    'bg-red-100 dark:bg-red-900/30'
                  }`}>
                    <Badge className="w-full justify-center mb-2" variant={
                      unitEconomics.health?.status === 'excellent' || unitEconomics.health?.status === 'good' ? 'default' :
                      unitEconomics.health?.status === 'warning' ? 'secondary' : 'destructive'
                    }>
                      {unitEconomics.health?.status?.toUpperCase()}
                    </Badge>
                    <p className="text-sm text-center">{unitEconomics.health?.note}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Breakeven Calculator */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Breakeven Calculator
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Fixed Costs (Monthly)</Label>
                    <Input
                      type="number"
                      value={breakevenInputs.fixed_costs_monthly}
                      onChange={(e) => setBreakevenInputs({ ...breakevenInputs, fixed_costs_monthly: e.target.value })}
                      placeholder="Rent, salaries, utilities..."
                    />
                    {breakevenDefaults && (
                      <p className="text-xs text-muted-foreground">{breakevenDefaults.notes?.fixed_costs}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Variable Cost % (COGS)</Label>
                    <Input
                      type="number"
                      value={breakevenInputs.variable_cost_percent}
                      onChange={(e) => setBreakevenInputs({ ...breakevenInputs, variable_cost_percent: e.target.value })}
                      placeholder="Cost of goods as % of revenue"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Average Ticket (optional)</Label>
                    <Input
                      type="number"
                      value={breakevenInputs.avg_ticket}
                      onChange={(e) => setBreakevenInputs({ ...breakevenInputs, avg_ticket: e.target.value })}
                      placeholder="Leave blank to use historical"
                    />
                  </div>
                  <Button onClick={calculateBreakeven} className="w-full">
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate Breakeven
                  </Button>
                </div>

                {breakevenResult && (
                  <div className="space-y-4">
                    <div className="p-4 bg-primary/10 rounded-lg">
                      <p className="text-sm text-muted-foreground text-center">Breakeven Point</p>
                      <p className="text-3xl font-bold text-center text-primary">
                        {formatCurrency(breakevenResult.breakeven?.revenue_per_day)}/day
                      </p>
                      <p className="text-sm text-center text-muted-foreground">
                        {breakevenResult.breakeven?.orders_per_day} orders/day
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-3 bg-muted/50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Monthly Breakeven</p>
                        <p className="font-bold">{formatCurrency(breakevenResult.breakeven?.revenue_per_month)}</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Contribution Margin</p>
                        <p className="font-bold">{breakevenResult.unit_economics?.contribution_margin_percent}%</p>
                      </div>
                    </div>
                    <div className={`p-3 rounded-lg ${
                      breakevenResult.margin_of_safety?.status === 'healthy' ? 'bg-green-100 dark:bg-green-900/30' :
                      breakevenResult.margin_of_safety?.status === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30' :
                      'bg-red-100 dark:bg-red-900/30'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Margin of Safety</span>
                        <span className="font-bold">{breakevenResult.margin_of_safety?.percent}%</span>
                      </div>
                      <p className="text-xs mt-1">
                        {breakevenResult.margin_of_safety?.above_breakeven
                          ? `${breakevenResult.margin_of_safety.percent}% above breakeven`
                          : 'Below breakeven'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Velocity Tab */}
        <TabsContent value="velocity" className="space-y-4">
          {/* Hourly Heatmap */}
          {hourlyData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Hourly Sales Heatmap
                </CardTitle>
                <CardDescription>Last {hourlyData.period?.days} days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-12 gap-1">
                    {hourlyData.hourly_data?.filter(h => h.hour >= 6 && h.hour <= 21).map((hour) => (
                      <div
                        key={hour.hour}
                        className={`aspect-square rounded flex items-center justify-center text-xs font-medium ${intensityColors[hour.intensity]} ${
                          hour.intensity === 'very_high' || hour.intensity === 'high' ? 'text-white' : 'text-foreground'
                        }`}
                        title={`${hour.hour_label}: ${formatCurrency(hour.revenue)} (${hour.orders} orders)`}
                      >
                        {hour.hour}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center gap-4 text-xs">
                    <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-gray-200 dark:bg-gray-700" /> Low</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-yellow-400 dark:bg-yellow-600" /> Medium</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-green-600 dark:bg-green-500" /> High</span>
                  </div>

                  {/* Peak Hours */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <p className="text-sm font-medium text-green-700 dark:text-green-400">Peak Hours</p>
                      {hourlyData.peak_hours?.slice(0, 3).map((peak, idx) => (
                        <div key={idx} className="flex justify-between text-sm mt-1">
                          <span>{peak.hour_label}</span>
                          <span className="font-medium">{formatCurrency(peak.revenue)} ({peak.revenue_percent}%)</span>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Slow Hours</p>
                      {hourlyData.slow_hours?.slice(0, 3).map((slow, idx) => (
                        <div key={idx} className="flex justify-between text-sm mt-1">
                          <span>{slow.hour_label}</span>
                          <span className="font-medium">{formatCurrency(slow.revenue)} ({slow.revenue_percent}%)</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Suggestions */}
                  {hourlyData.suggestions?.length > 0 && (
                    <div className="space-y-2 mt-4">
                      {hourlyData.suggestions.map((sug, idx) => (
                        <div key={idx} className={`p-2 rounded text-sm ${
                          sug.type === 'peak_staffing' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-amber-50 dark:bg-amber-900/20'
                        }`}>
                          <span className="font-medium">{sug.hour}:</span> {sug.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Daily Pattern */}
          {dailyPattern && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Day of Week Pattern
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dailyPattern.daily_data?.map((day) => (
                    <div key={day.day} className="flex items-center gap-3">
                      <span className="w-24 text-sm">{day.day_name}</span>
                      <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${day.revenue_percent}%` }}
                        />
                      </div>
                      <span className="w-20 text-sm text-right">{formatCurrency(day.avg_revenue)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm">
                    <span className="font-medium text-green-600">{dailyPattern.summary?.best_day}</span> is your busiest day ({formatCurrency(dailyPattern.summary?.best_day_avg)} avg).
                    Consider promotions on <span className="font-medium text-amber-600">{dailyPattern.summary?.worst_day}</span> ({formatCurrency(dailyPattern.summary?.worst_day_avg)} avg).
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Product Velocity */}
          {productVelocity && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="w-5 h-5 text-green-600" />
                    Fast Movers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {productVelocity.fast_movers?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No data yet</p>
                  ) : (
                    <div className="space-y-2">
                      {productVelocity.fast_movers?.slice(0, 5).map((product, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded">
                          <span className="font-medium truncate flex-1">{product.name}</span>
                          <span className="text-sm text-muted-foreground ml-2">{product.daily_velocity}/day</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-amber-600" />
                    Slow Movers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {productVelocity.slow_movers?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No data yet</p>
                  ) : (
                    <div className="space-y-2">
                      {productVelocity.slow_movers?.slice(0, 5).map((product, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
                          <span className="font-medium truncate flex-1">{product.name}</span>
                          <span className="text-sm text-muted-foreground ml-2">{product.daily_velocity}/day</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {productVelocity.suggestions?.length > 0 && (
                    <div className="mt-4 p-2 bg-red-50 dark:bg-red-900/20 rounded">
                      <p className="text-sm font-medium text-red-600">Consider 86-ing:</p>
                      <ul className="text-sm mt-1 space-y-1">
                        {productVelocity.suggestions.slice(0, 3).map((sug, idx) => (
                          <li key={idx}>{sug.product} ({sug.daily_sales}/day)</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* SOP Modal */}
      <SOPFormModal
        isOpen={showSOPModal}
        onClose={() => { setShowSOPModal(false); setEditingSOP(null); }}
        onSave={handleSaveSOP}
        editingSOP={editingSOP}
        categories={sopCategories}
      />
    </div>
  );
}
